import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ApiService } from '../../services';
import {
  Order,
  OrderStatus,
  PaymentMethod,
  EventModel,
  PaginationResponse,
} from '../../models';
import { HttpResponse } from '@angular/common/http';
import { EventsListComponent } from '../../components/events-list/events-list.component';
import { AuthStateService, getEmailFromToken } from '@hypenation/auth';

@Component({
  selector: 'hypenation-tickets-view-past-tickets',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzIconModule,
    NzCardModule,
    NzPaginationModule,
    NzTagModule,
    NzEmptyModule,
    NzSpinModule,
    EventsListComponent,
  ],
  templateUrl: './view-past-tickets.component.html',
  styleUrl: './view-past-tickets.component.css',
})
export class ViewPastTicketsComponent implements OnInit {
  loading = false;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  allOrders: Order[] = [];
  displayedOrders: Order[] = [];
  userEmail: string | null = null;
  
  // Featured events properties
  featuredEvents: EventModel[] = [];
  featuredEventsLoading = false;
  featuredEventsLoadingMore = false;
  featuredEventsTotalItems = 0;
  featuredEventsPage = 0;
  featuredEventsSize = 30;

  get featuredEventsHasMore(): boolean {
    return this.featuredEvents.length < this.featuredEventsTotalItems;
  }

  constructor(
    private readonly _apiService: ApiService,
    private readonly _authStateService: AuthStateService
  ) {
    // Load featured events on component init
    this.getFeaturedEvents();
  }

  ngOnInit(): void {
    // Get email from token
    const token = this._authStateService.getToken();
    if (token) {
      this.userEmail = getEmailFromToken(token);
      if (this.userEmail) {
        // Automatically load orders for the authenticated user
        this.loadOrders();
      }
    }
  }

  public loadOrders(): void {
    if (!this.userEmail) return;
    
    this.loading = true;
    this.currentPage = 1; // Reset to first page
    
    // Sort by orderDate descending (newest first)
    const sortParam = 'orderDate,desc';
    
    this._apiService
      .getAllMyOrdersByEmail(this.userEmail, sortParam)
      .subscribe({
        next: (res: HttpResponse<Order[]>) => {
          // Response is an array of orders
          // Filter to only show completed orders
          const allOrders = res.body ?? [];
          this.allOrders = allOrders.filter(order => order.status === OrderStatus.COMPLETED);
          this.totalItems = this.allOrders.length;
          this.updateDisplayedOrders();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
        complete: () => (this.loading = false),
      });
  }

  private updateDisplayedOrders(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.displayedOrders = this.allOrders.slice(startIndex, endIndex);
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.updateDisplayedOrders();
  }

  public onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1; // Reset to first page when changing page size
    this.updateDisplayedOrders();
  }

  public getStatusColor(status: OrderStatus): string {
    switch (status) {
      case OrderStatus.COMPLETED:
        return 'success';
      case OrderStatus.CANCELLED:
        return 'error';
      case OrderStatus.IN_PROGRESS:
        return 'processing';
      case OrderStatus.RESERVED:
        return 'warning';
      default:
        return 'default';
    }
  }

  public getPaymentMethodDisplay(method: PaymentMethod): string {
    switch (method) {
      case PaymentMethod.CASH:
        return 'Cash';
      case PaymentMethod.ECOCASH:
        return 'EcoCash';
      case PaymentMethod.INNBUCKS:
        return 'InnBucks';
      case PaymentMethod.ONE_MONEY:
        return 'OneMoney';
      case PaymentMethod.OMARI:
        return 'Omari';
      case PaymentMethod.VISA:
        return 'Visa';
      case PaymentMethod.MASTER_CARD:
        return 'MasterCard';
      case PaymentMethod.LOCAL_BANK_CARD:
        return 'Local Bank Card';
      case PaymentMethod.LOCAL_BANK_TRANSFER:
        return 'Bank Transfer';
      case PaymentMethod.FREE:
        return 'Free';
      case PaymentMethod.ONLINE:
        return 'Online';
      default:
        return method;
    }
  }

  public formatDate(dateValue: string | number | null | undefined): string {
    if (!dateValue) return 'N/A';
    
    // Handle both string dates and timestamp numbers
    const date = typeof dateValue === 'number' 
      ? new Date(dateValue) 
      : new Date(dateValue);
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  public formatCurrency(amount: number, currencyCode = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  }

  public getFeaturedEvents(): void {
    this.featuredEventsLoading = true;
    this.featuredEventsPage = 0;
    this._apiService
      .getFeaturedEvents(0, this.featuredEventsSize)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          this.featuredEvents = res.body?.content ?? [];
          this.featuredEventsTotalItems = res.body?.totalElements ?? 0;
          this.featuredEventsLoading = false;
        },
        error: () => {
          this.featuredEventsLoading = false;
        },
        complete: () => (this.featuredEventsLoading = false),
      });
  }

  public loadMoreFeatured(): void {
    if (!this.featuredEventsHasMore || this.featuredEventsLoadingMore) return;
    const nextPage = this.featuredEventsPage + 1;
    this.featuredEventsLoadingMore = true;
    this._apiService
      .getFeaturedEvents(nextPage, this.featuredEventsSize)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          const incoming = res.body?.content ?? [];
          const seen = new Set(this.featuredEvents.map((e) => e.uid));
          const filtered = incoming.filter((e) => !seen.has(e.uid));
          this.featuredEvents = [...this.featuredEvents, ...filtered];
          this.featuredEventsTotalItems =
            res.body?.totalElements ?? this.featuredEventsTotalItems;
          this.featuredEventsPage = nextPage;
          this.featuredEventsLoadingMore = false;
        },
        error: () => (this.featuredEventsLoadingMore = false),
      });
  }
}
